import { useEffect, useState } from 'react';
import { useApi } from './hooks/useApi';
import { useOfficeContext, type AppMode } from './hooks/useOfficeContext';
import { api, setAuthMode, Branding } from './api/client';
import { getAccount } from './api/msal';
import { BootstrapWizard } from './views/BootstrapWizard';
import { SlideLibrary } from './views/SlideLibrary';
import { ErrorScreen } from './views/ErrorScreen';
import { SignInScreen } from './views/SignInScreen';
import { applyAccentTheme } from './theme';

type WebAuthState = 'checking' | 'unauthed' | 'authed';

export default function App() {
  const office = useOfficeContext();
  const [webAuthState, setWebAuthState] = useState<WebAuthState>('checking');

  useEffect(() => {
    if (!office.initialized) return;
    setAuthMode(office.mode);
    if (office.mode === 'office') {
      setWebAuthState('authed'); // Office.js handles auth implicitly
    } else {
      getAccount()
        .then((acct) => setWebAuthState(acct ? 'authed' : 'unauthed'))
        .catch(() => setWebAuthState('unauthed'));
    }
  }, [office.initialized, office.mode]);

  if (!office.initialized || webAuthState === 'checking') {
    return <LoadingScreen />;
  }

  if (office.mode === 'web' && webAuthState === 'unauthed') {
    return <SignInScreen onSignedIn={() => setWebAuthState('authed')} />;
  }

  return <AuthedApp mode={office.mode} host={office.host} />;
}

function AuthedApp({ mode, host }: { mode: AppMode; host: string }) {
  const { data: me, loading, error, refetch } = useApi(() => api.me());
  // Branding fetch is gated on `me.setupComplete` because the /branding doc
  // is created by /bootstrap. Polling once a minute is enough — the form
  // refetches manually on save.
  const { data: branding, refetch: refetchBranding } = useApi<Branding>(
    () => api.getBranding(),
    {
      deps: [me?.setupComplete],
      pollMs: me?.setupComplete ? 60_000 : undefined,
    },
  );

  // Whenever branding lands or changes, push the accent into CSS variables
  // so every Tailwind `accent-*` utility on the page picks up the new colour.
  useEffect(() => {
    if (branding?.accentColor) applyAccentTheme(branding.accentColor);
  }, [branding?.accentColor]);

  if (loading) return <LoadingScreen />;

  if (error) {
    return (
      <ErrorScreen
        title="Couldn't load Slide Library"
        message={error.message}
        action={{ label: 'Retry', onClick: refetch }}
      />
    );
  }
  if (!me) {
    return <ErrorScreen title="Authentication failed" message="Please reopen the add-in." />;
  }

  if (!me.setupComplete) {
    if (!me.canBootstrap) {
      return (
        <ErrorScreen
          title="Slide Library isn't set up yet"
          message="Ask a Global Administrator in your organisation to complete the first-time setup."
        />
      );
    }
    return <BootstrapWizard onComplete={refetch} />;
  }

  return <SlideLibrary me={me} mode={mode} host={host} branding={branding ?? null} onBrandingChange={refetchBranding} />;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-500 text-sm">Loading Slide Library…</div>
    </div>
  );
}
